import { useRuntimeConfig } from '#imports'
import type { RouteRecordRaw } from 'vue-router'

import type { Market } from '@backmarket/http-api/src/api-models/Market'
import buybackButtonEnabled from '@backmarket/nuxt-layer-buyback/app/middleware/buybackButtonEnabled'

import { generateWebviewRoutes } from '~/scopes/webviews/generateWebviewRoutes'
import redirectIfWebview from '~/scopes/webviews/middleware/redirectIfWebview'

import { routeHasValidParams } from './navigation/routeHasValidParams'
import { CMS } from './routes-names'

export default ({ market }: { market: Market }): RouteRecordRaw[] => {
  const { FF_ENABLE_CMS_CONTENT_HUB, FF_CMS_ENABLE_PROMISE_PAGE_COUNTRIES } =
    useRuntimeConfig().public

  const enableCMSContentHub = FF_ENABLE_CMS_CONTENT_HUB?.includes(
    market.countryCode,
  )

  const enableCMSPromisePage = FF_CMS_ENABLE_PROMISE_PAGE_COUNTRIES?.includes(
    market.countryCode,
  )

  const cmsRoutes: RouteRecordRaw[] = [
    {
      path: 'about-us',
      name: CMS.ABOUT_US,
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Corporate' },
      },
    },
    {
      name: CMS.QUALITY,
      path: 'quality',
      // when cms promise is disabled, we perform a server navigation and hit creative-pages (and we don't render Landing.vue)
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        disableClientSideNavigation: () => !enableCMSPromisePage,
        owners: ['bot-squad-content-front'],
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Corporate' },
      },
    },
    {
      name: CMS.ARTICLE_ROOT_HUB,
      path: 'c',
      component: () => {
        if (enableCMSContentHub) return import('~/scopes/cms/Landing.vue')

        return import('~/scopes/article/Hub/pages/ContentHub.vue')
      },
      meta: {
        owners: ['bot-squad-content-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Content' },
      },
    },
    {
      name: CMS.ARTICLE_HUB,
      // note the backend always return 'hub_buying_guide'. We need to use that for category hub and fix the breadcrumb logic for the root
      path: 'c/hub/:pageCategory',
      component: () => import('~/scopes/article/Hub/pages/ContentHub.vue'),
      meta: {
        owners: ['bot-squad-content-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
        routeExists: routeHasValidParams,
        tracking: { contentGroup: 'Content' },
      },
    },
    {
      name: CMS.ARTICLE,
      path: 'c/:category/:pageName',
      component: () => import('~/scopes/article/Article/ArticleProxy.vue'),
      meta: {
        owners: ['bot-squad-content-front'],
        middleware: [redirectIfWebview],
        routeExists: routeHasValidParams,
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Content' },
      },
    },

    {
      name: CMS.BACK_STORIES,
      path: 'back-stories/:pageName',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front'],
        middleware: [redirectIfWebview],
        routeExists: routeHasValidParams,
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Content' },
      },
    },
    {
      name: CMS.BACK_STORIES_CATEGORY,
      path: 'back-stories/:pageName/:pageCategory',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front'],
        middleware: [redirectIfWebview],
        routeExists: routeHasValidParams,
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Content' },
      },
    },

    {
      name: CMS.BRAND_STORE,
      path: 'b/:pageName',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front'],
        middleware: [redirectIfWebview],
        routeExists: routeHasValidParams,
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Brand Store' },
      },
    },
    {
      name: CMS.BRAND_STORE_CATEGORY,
      path: 'b/:pageName/:pageCategory',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front'],
        middleware: [redirectIfWebview],
        routeExists: routeHasValidParams,

        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Brand Store' },
      },
    },

    {
      name: CMS.BUYBACK,
      path: 'buyback/:pageName',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: [
          'bot-squad-content-front',
          'bot-squad-circularity-offer-front',
        ],
        middleware: [redirectIfWebview, buybackButtonEnabled],
        routeExists: routeHasValidParams,
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Trade-In Discovery' },
      },
    },

    {
      name: CMS.BUYBACK_CATEGORY,
      path: 'buyback/:pageName/:pageCategory',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: [
          'bot-squad-content-front',
          'bot-squad-circularity-offer-front',
        ],
        middleware: [redirectIfWebview, buybackButtonEnabled],
        routeExists: routeHasValidParams,
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Trade-In Discovery' },
      },
    },
    {
      name: CMS.CAREERS_HOME,
      path: 'careers/:pageName',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front'],
        middleware: [redirectIfWebview],
        routeExists: routeHasValidParams,
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Careers' },
      },
    },

    {
      name: CMS.CAREERS_CATEGORY,
      path: 'careers/:pageName/:pageCategory',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front'],
        middleware: [redirectIfWebview],
        routeExists: routeHasValidParams,
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Careers' },
      },
    },

    {
      name: CMS.EVENT,
      path: 'e/:pageName',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front'],
        middleware: [redirectIfWebview],
        routeExists: routeHasValidParams,
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Promotional' },
      },
    },
    {
      name: CMS.GIFT_FINDER_HOME,
      path: 'gift',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front'],
        middleware: [redirectIfWebview],
        tracking: { contentGroup: 'Gift' },
      },
    },
    {
      name: CMS.GIFT_FINDER_MAIN,
      path: 'gift/:pageName',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Gift' },
      },
    },
    {
      name: CMS.GIFT_FINDER_EVENT,
      path: 'gift/:pageName/:pageCategory',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front'],
        middleware: [redirectIfWebview],
        routeExists: routeHasValidParams,
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Gift' },
      },
    },

    {
      name: CMS.REFER_FRIEND,
      path: 'refer-friend',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-squad-lifecycle-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Promotional' },
      },
    },

    {
      name: CMS.SELLER,
      path: 'seller/:pageName',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        routeExists: routeHasValidParams,
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Marketplace' },
      },
    },

    {
      name: CMS.SERVICE,
      path: 'service/:pageName',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        routeExists: routeHasValidParams,
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Corporate' },
      },
    },

    {
      name: CMS.STATEMENT,
      path: 'accessibility-statement',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Corporate' },
      },
    },

    {
      name: CMS.END_FAST_TECH,
      path: 'end-fast-tech',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Corporate' },
      },
    },

    {
      name: CMS.GIFTS,
      path: 'gifts',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Gift' },
      },
    },

    {
      name: CMS.REPAIR_AND_CARE,
      path: 'repair-and-care',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Corporate' },
      },
    },

    {
      name: CMS.E2E_PAGE,
      path: 'e2e/:pageName',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        routeExists: routeHasValidParams,
        tracking: { contentGroup: 'Internal' },
      },
    },

    {
      name: CMS.LIFESTYLE,
      path: 'lf/:pageName',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: [
          'bot-squad-content-front',
          'bot-squad-search-and-recommendation-front',
        ],
        middleware: [redirectIfWebview],
        routeExists: routeHasValidParams,
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Content' },
      },
    },

    {
      name: CMS.EXPRESS_REPLACEMENT,
      path: 'express-replacement',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Express Replacement' },
      },
    },

    {
      name: CMS.CUSTOMER_CARE,
      path: 'customer-care',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front', 'bot-group-onboard-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
      },
    },

    {
      name: CMS.TRANSLATIONS,
      path: 'translations',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Corporate' },
      },
    },

    {
      name: CMS.PARTNERSHIP_HUB,
      path: 'partnership-hub',
      component: () => import('~/scopes/cms/Landing.vue'),
      meta: {
        owners: ['bot-squad-content-front'],
        middleware: [redirectIfWebview],
        cache: 'public, max-age=43200',
        tracking: { contentGroup: 'Corporate' },
      },
    },
  ]

  const webviewCMSRoutes = generateWebviewRoutes(cmsRoutes)

  return [...cmsRoutes, ...webviewCMSRoutes]
}
